import { useMemo, useState, useEffect, useCallback } from 'react';
import { Dialog, Paper, Stack, TextField, Typography, CircularProgress, Chip, MenuItem } from '@mui/material';
import { StyledButton } from '../../components';
import { ConfirmationDialog } from '../../components/modals/ConfirmDialog';
import {
  QmLocationAdminOption,
  listQmReferenceLocationsAdminRequest,
  createQmReferenceLocationRequest,
  updateQmReferenceLocationRequest,
  archiveQmReferenceLocationRequest
} from '../../api/qmCrud';
import { useAlert } from '../../hooks';
import { captureError } from '../../common/sentry';

type QmInboundLocationSettingsDialogProps = {
  open: boolean;
  canManage: boolean;
  onClose: () => void;
};

export default function QmInboundLocationSettingsDialog({
  open,
  canManage,
  onClose
}: QmInboundLocationSettingsDialogProps) {
  const alert = useAlert();

  const [qmInboundLocationSettings, setQmInboundLocationSettings] = useState<QmLocationAdminOption[]>([]);
  const [isQmInboundLocationSettingsLoading, setIsQmInboundLocationSettingsLoading] = useState(false);
  const [locationSearchText, setLocationSearchText] = useState('');
  const [locationStatusFilter, setLocationStatusFilter] = useState<'ALL' | 'ACTIVE' | 'ARCHIVED'>('ALL');
  const [savingLocationCode, setSavingLocationCode] = useState<string | null>(null);
  const [archivingLocationCode, setArchivingLocationCode] = useState<string | null>(null);
  const [archiveConfirmLocationCode, setArchiveConfirmLocationCode] = useState<string | null>(null);
  const [reactivatingLocationCode, setReactivatingLocationCode] = useState<string | null>(null);
  const [newLocationCode, setNewLocationCode] = useState('');
  const [newLocationLabel, setNewLocationLabel] = useState('');
  const [newLocationDisplayOrder, setNewLocationDisplayOrder] = useState('');
  const [isCreatingLocation, setIsCreatingLocation] = useState(false);

  const loadQmInboundLocationSettings = useCallback(async () => {
    if (!canManage) return;

    try {
      setIsQmInboundLocationSettingsLoading(true);
      const locations = await listQmReferenceLocationsAdminRequest();
      setQmInboundLocationSettings(locations);
    } catch (error) {
      alert('Unable to load QM inbound location settings', 'error');
      captureError(error, 'settings-loadQmInboundLocationSettings');
    } finally {
      setIsQmInboundLocationSettingsLoading(false);
    }
  }, [alert, canManage]);

  useEffect(() => {
    if (open) {
      void loadQmInboundLocationSettings();
    }
  }, [loadQmInboundLocationSettings, open]);

  function updateQmInboundLocationDraft(code: string, patch: Partial<QmLocationAdminOption>) {
    setQmInboundLocationSettings((previous) =>
      previous.map((location) => (location.code === code ? { ...location, ...patch } : location))
    );
  }

  async function saveQmInboundLocation(location: QmLocationAdminOption) {
    try {
      setSavingLocationCode(location.code);
      await updateQmReferenceLocationRequest({
        code: location.code,
        label: location.label.trim(),
        displayOrder: Number(location.displayOrder)
      });
      alert('QM inbound location updated', 'success');
      await loadQmInboundLocationSettings();
    } catch (error) {
      alert('Unable to update QM inbound location', 'error');
      captureError(error, 'settings-saveQmInboundLocation');
    } finally {
      setSavingLocationCode(null);
    }
  }

  async function archiveQmInboundLocation(code: string) {
    try {
      setArchivingLocationCode(code);
      await archiveQmReferenceLocationRequest({ code });
      alert('QM inbound location archived', 'success');
      await loadQmInboundLocationSettings();
    } catch (error) {
      alert('Unable to archive QM inbound location', 'error');
      captureError(error, 'settings-archiveQmInboundLocation');
    } finally {
      setArchivingLocationCode(null);
    }
  }

  async function reactivateQmInboundLocation(location: QmLocationAdminOption) {
    try {
      setReactivatingLocationCode(location.code);
      await createQmReferenceLocationRequest({
        code: location.code,
        label: location.label.trim(),
        displayOrder: Number(location.displayOrder)
      });
      alert('QM inbound location reactivated', 'success');
      await loadQmInboundLocationSettings();
    } catch (error) {
      alert('Unable to reactivate QM inbound location', 'error');
      captureError(error, 'settings-reactivateQmInboundLocation');
    } finally {
      setReactivatingLocationCode(null);
    }
  }

  async function createQmInboundLocation() {
    const code = newLocationCode.trim();
    const label = newLocationLabel.trim();
    const hasDisplayOrder = newLocationDisplayOrder.trim().length > 0;
    const displayOrder = Number(newLocationDisplayOrder);

    if (!code || !label) {
      alert('Location code and label are required.', 'warning');
      return;
    }

    if (hasDisplayOrder && (!Number.isFinite(displayOrder) || displayOrder < 0)) {
      alert('Display order must be a valid number.', 'warning');
      return;
    }

    try {
      setIsCreatingLocation(true);
      await createQmReferenceLocationRequest({
        code,
        label,
        ...(hasDisplayOrder ? { displayOrder } : {})
      });
      setNewLocationCode('');
      setNewLocationLabel('');
      setNewLocationDisplayOrder('');
      alert('QM inbound location created', 'success');
      await loadQmInboundLocationSettings();
    } catch (error) {
      alert('Unable to create QM inbound location', 'error');
      captureError(error, 'settings-createQmInboundLocation');
    } finally {
      setIsCreatingLocation(false);
    }
  }

  const locationCounts = useMemo(() => {
    const active = qmInboundLocationSettings.filter((item) => item.active).length;
    const archived = qmInboundLocationSettings.length - active;
    return {
      total: qmInboundLocationSettings.length,
      active,
      archived
    };
  }, [qmInboundLocationSettings]);

  const filteredQmInboundLocationSettings = useMemo(() => {
    const query = locationSearchText.trim().toLowerCase();
    return qmInboundLocationSettings.filter((location) => {
      if (locationStatusFilter === 'ACTIVE' && !location.active) return false;
      if (locationStatusFilter === 'ARCHIVED' && location.active) return false;

      if (!query) return true;
      return location.code.toLowerCase().includes(query) || location.label.toLowerCase().includes(query);
    });
  }, [locationSearchText, locationStatusFilter, qmInboundLocationSettings]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'rgba(148,163,184,0.35)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
            boxShadow: '0 18px 36px rgba(15, 23, 42, 0.16)'
          }
        }}>
        <Stack spacing={1.5} sx={{ p: 2.25 }}>
          <Stack spacing={0.4}>
            <Typography sx={{ fontSize: '1.12rem', fontWeight: 800, color: '#0f172a' }}>
              QM Inbound location list settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Archived locations stay visible for history and can be reactivated.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color="default"
              label={`Total ${locationCounts.total}`}
              sx={{ fontWeight: 700, borderRadius: 1.5, bgcolor: '#e2e8f0', color: '#334155' }}
            />
            <Chip
              size="small"
              color="success"
              label={`Active ${locationCounts.active}`}
              sx={{ fontWeight: 700, borderRadius: 1.5 }}
            />
            <Chip
              size="small"
              color="warning"
              label={`Archived ${locationCounts.archived}`}
              sx={{ fontWeight: 700, borderRadius: 1.5 }}
            />
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: 'rgba(148,163,184,0.32)',
              backgroundColor: 'background.paper'
            }}>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, mb: 1, color: '#0f172a' }}>Add Location</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <TextField
                size="small"
                label="Code"
                value={newLocationCode}
                onChange={(event) => setNewLocationCode(event.target.value)}
                sx={{ width: { xs: '100%', md: 140 } }}
              />
              <TextField
                size="small"
                label="Label"
                value={newLocationLabel}
                onChange={(event) => setNewLocationLabel(event.target.value)}
                sx={{ flex: 1, minWidth: { xs: '100%', md: 320 } }}
              />
              <TextField
                size="small"
                type="number"
                label="Display Order"
                value={newLocationDisplayOrder}
                onChange={(event) => setNewLocationDisplayOrder(event.target.value)}
                inputProps={{ min: 0 }}
                sx={{ width: { xs: '100%', md: 160 } }}
              />
              <StyledButton
                variant="contained"
                onClick={() => void createQmInboundLocation()}
                disabled={isCreatingLocation}
                sx={{ minWidth: { xs: '100%', md: 96 } }}>
                {isCreatingLocation ? 'Adding...' : 'Add'}
              </StyledButton>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: 'rgba(148,163,184,0.32)',
              background: 'rgba(255,255,255,0.92)'
            }}>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, mb: 1, color: '#0f172a' }}>
              Location List
            </Typography>
            {isQmInboundLocationSettingsLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : qmInboundLocationSettings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No QM inbound locations found.
              </Typography>
            ) : (
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    label="Search by code or label"
                    value={locationSearchText}
                    onChange={(event) => setLocationSearchText(event.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={locationStatusFilter}
                    onChange={(event) => setLocationStatusFilter(event.target.value as 'ALL' | 'ACTIVE' | 'ARCHIVED')}
                    sx={{ width: { xs: '100%', md: 180 } }}>
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="ARCHIVED">Archived</MenuItem>
                  </TextField>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    px: 1,
                    display: { xs: 'none', md: 'flex' },
                    '& .MuiTypography-root': { fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700 }
                  }}>
                  <Typography sx={{ width: 140 }}>Code</Typography>
                  <Typography sx={{ minWidth: 320, flex: 1 }}>Label</Typography>
                  <Typography sx={{ width: 160 }}>Display Order</Typography>
                  <Typography sx={{ width: 90 }}>Status</Typography>
                  <Typography sx={{ width: 255, textAlign: 'right' }}>Actions</Typography>
                </Stack>

                {filteredQmInboundLocationSettings.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                    No locations match the current filters.
                  </Typography>
                ) : null}

                {filteredQmInboundLocationSettings.map((location) => (
                  <Stack
                    key={location.code}
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: location.active ? 'divider' : 'warning.light',
                      backgroundColor: location.active ? 'background.paper' : '#fffaf0',
                      flexWrap: 'wrap',
                      rowGap: 1,
                      transition: 'all 160ms ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)'
                      }
                    }}>
                    <TextField
                      size="small"
                      label="Code"
                      value={location.code}
                      disabled
                      sx={{ width: { xs: '100%', md: 140 } }}
                    />
                    <TextField
                      size="small"
                      label="Label"
                      value={location.label}
                      onChange={(event) => updateQmInboundLocationDraft(location.code, { label: event.target.value })}
                      disabled={!location.active}
                      sx={{ flex: 1, minWidth: { xs: '100%', md: 320 } }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Display Order"
                      value={location.displayOrder}
                      onChange={(event) =>
                        updateQmInboundLocationDraft(location.code, {
                          displayOrder: Number(event.target.value || 0)
                        })
                      }
                      disabled={!location.active}
                      inputProps={{ min: 0 }}
                      sx={{ width: { xs: '100%', md: 160 } }}
                    />
                    <Chip
                      size="small"
                      color={location.active ? 'success' : 'default'}
                      label={location.active ? 'Active' : 'Archived'}
                    />
                    <StyledButton
                      variant="outlined"
                      onClick={() => void saveQmInboundLocation(location)}
                      disabled={!location.active || savingLocationCode === location.code}
                      sx={{ minWidth: 100 }}>
                      {savingLocationCode === location.code ? 'Saving...' : 'Save'}
                    </StyledButton>
                    <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' }, flexWrap: 'nowrap' }}>
                      {location.active ? (
                        <StyledButton
                          variant="outlined"
                          color="warning"
                          onClick={() => setArchiveConfirmLocationCode(location.code)}
                          disabled={archivingLocationCode === location.code}
                          sx={{ minWidth: 112 }}>
                          {archivingLocationCode === location.code ? 'Archiving...' : 'Archive'}
                        </StyledButton>
                      ) : (
                        <StyledButton
                          variant="outlined"
                          color="success"
                          onClick={() => void reactivateQmInboundLocation(location)}
                          disabled={reactivatingLocationCode === location.code}
                          sx={{ minWidth: 112 }}>
                          {reactivatingLocationCode === location.code ? 'Reactivating...' : 'Reactivate'}
                        </StyledButton>
                      )}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>

          <Stack direction="row" justifyContent="flex-end">
            <StyledButton variant="outlined" onClick={onClose}>
              Close
            </StyledButton>
          </Stack>
        </Stack>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(archiveConfirmLocationCode)}
        onClose={() => setArchiveConfirmLocationCode(null)}
        onConfirm={async () => {
          const code = archiveConfirmLocationCode;
          setArchiveConfirmLocationCode(null);
          if (code) {
            await archiveQmInboundLocation(code);
          }
        }}
        title="Archive Location"
        content="Are you sure you want to archive this location? You can reactivate it later."
      />
    </>
  );
}
